import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Compass, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary";

function StepRow({ s, onSave, onDelete, onMove }) {
  const [d, setD] = useState(s);
  const dirty = d.title !== s.title || d.body !== s.body || d.target !== s.target || d.route !== s.route;
  useEffect(() => { setD(s); }, [s]);
  return (
    <div className="rounded-lg border border-line bg-bg-elevated/40 p-3 space-y-2" data-testid={`tourmgr-step-${s.step_id}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-ink-muted w-6">#{s.order}</span>
        <input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })}
          data-testid={`tourmgr-title-${s.step_id}`} className={`${inputCls} font-semibold`} />
        <label className="flex items-center gap-1 text-[10px] text-ink-muted shrink-0">
          <input type="checkbox" checked={!!s.active} data-testid={`tourmgr-active-${s.step_id}`}
            onChange={(e) => onSave(s.step_id, { active: e.target.checked })} /> active
        </label>
        <button onClick={() => onMove(s.step_id, "up")} data-testid={`tourmgr-up-${s.step_id}`}
          className="rounded border border-line p-1 text-ink-muted hover:text-ink-primary"><ArrowUp className="h-3 w-3" /></button>
        <button onClick={() => onMove(s.step_id, "down")} data-testid={`tourmgr-down-${s.step_id}`}
          className="rounded border border-line p-1 text-ink-muted hover:text-ink-primary"><ArrowDown className="h-3 w-3" /></button>
        <button onClick={() => onDelete(s.step_id)} data-testid={`tourmgr-del-${s.step_id}`}
          className="rounded border border-line p-1 text-rose-300"><Trash2 className="h-3 w-3" /></button>
      </div>
      <textarea value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} rows={2}
        data-testid={`tourmgr-body-${s.step_id}`} className={inputCls} />
      <div className="flex gap-2">
        <input value={d.target} onChange={(e) => setD({ ...d, target: e.target.value })}
          data-testid={`tourmgr-target-${s.step_id}`} placeholder="target data-testid" className={`${inputCls} font-mono`} />
        <input value={d.route || ""} onChange={(e) => setD({ ...d, route: e.target.value })}
          data-testid={`tourmgr-route-${s.step_id}`} placeholder="/dashboard/…" className={`${inputCls} font-mono`} />
        {dirty && (
          <button onClick={() => onSave(s.step_id, { title: d.title, body: d.body, target: d.target, route: d.route })}
            data-testid={`tourmgr-save-${s.step_id}`}
            className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 text-[11px] font-bold text-black shrink-0">
            <Save className="h-3 w-3" /> Save
          </button>
        )}
      </div>
    </div>
  );
}

const GUIDE_TITLES = {
  "welcome": "Welcome to BroadcastFlow AI", "add-your-first-station": "Add Your First Station",
  "connect-your-stream": "Connect Your Stream", "connect-social-media": "Connect Social Media",
  "create-your-first-ai-post": "Create Your First AI Post", "understanding-credits": "Understanding Credits",
  "create-your-first-podcast": "Create Your First Podcast", "intro-music-video-studio": "Introduction to Music Video Studio",
  "intro-digital-magazine-studio": "Introduction to Digital Magazine Studio",
};

function GuideVideos() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => api.get("/tour/guide-videos").then((r) => {
    setData(r.data);
    const d = {};
    (r.data.slugs || []).forEach((s) => { d[s] = r.data.videos?.[s]?.video_url || ""; });
    setDrafts(d);
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const save = async (slug) => {
    try {
      await api.put(`/admin/tour/guide-videos/${slug}`, { video_url: drafts[slug] || "" });
      toast.success(drafts[slug] ? "Video saved" : "Video removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="rounded-lg border border-line bg-bg-elevated/40 p-3 space-y-2" data-testid="tourmgr-guide-videos">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
        Guide walkthrough videos — YouTube, Vimeo, or direct MP4 links shown inside Help → Getting Started
      </div>
      {(data.slugs || []).map((slug) => {
        const saved = data.videos?.[slug]?.video_url || "";
        const dirty = (drafts[slug] || "") !== saved;
        return (
          <div key={slug} className="flex items-center gap-2">
            <span className="w-56 shrink-0 text-[11px] text-ink-secondary truncate">{GUIDE_TITLES[slug] || slug}</span>
            <input value={drafts[slug] || ""} onChange={(e) => setDrafts({ ...drafts, [slug]: e.target.value })}
              placeholder="https://youtube.com/watch?v=…" data-testid={`tourmgr-gv-url-${slug}`}
              className={inputCls + " font-mono"} />
            {dirty && (
              <button onClick={() => save(slug)} data-testid={`tourmgr-gv-save-${slug}`}
                className="rounded-md bg-brand-amber px-3 py-1.5 text-[11px] font-bold text-black shrink-0">Save</button>
            )}
            {saved && !dirty && <span className="text-[10px] text-emerald-300 shrink-0">✓ set</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function TourManager() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", target: "", route: "" });

  const load = () => Promise.all([
    api.get("/admin/tour/steps").then((r) => setData(r.data)),
    api.get("/admin/tour/stats").then((r) => setStats(r.data)),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const err = (e) => toast.error(e?.response?.data?.detail || "Failed");
  const save = async (id, patch) => { try { await api.put(`/admin/tour/steps/${id}`, patch); load(); } catch (e) { err(e); } };
  const del = async (id) => { try { await api.delete(`/admin/tour/steps/${id}`); load(); } catch (e) { err(e); } };
  const move = async (id, direction) => { try { await api.post(`/admin/tour/steps/${id}/move`, { direction }); load(); } catch (e) { err(e); } };
  const add = async () => {
    try {
      await api.post("/admin/tour/steps", form);
      setForm({ title: "", body: "", target: "", route: "" });
      load();
    } catch (e) { err(e); }
  };
  const toggleEnabled = async (on) => {
    try { await api.put("/admin/tour/config", { enabled: on }); load(); } catch (e) { err(e); }
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4" data-testid="tour-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-ink-primary">
          <Compass className="h-4 w-4 text-brand-amber" /> Guided Tour Manager
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-secondary">
          Onboarding &amp; tour enabled
          <input type="checkbox" checked={data.config?.enabled !== false} data-testid="tourmgr-enabled"
            onChange={(e) => toggleEnabled(e.target.checked)} />
        </label>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tourmgr-stats">
          {[["Welcome seen", stats.welcome_seen], ["Tours started", stats.tour_started],
            ["Completed", stats.tour_completed], ["Skipped", stats.tour_skipped],
            ["Completion", `${stats.completion_pct}%`]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-line bg-bg-elevated/40 p-2.5">
              <div className="text-base font-bold text-ink-primary">{v}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {(data.steps || []).map((s) => (
          <StepRow key={s.step_id} s={s} onSave={save} onDelete={del} onMove={move} />
        ))}
      </div>

      <div className="rounded-lg border border-dashed border-line p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Add tour step</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Step title" data-testid="tourmgr-new-title" className={inputCls} />
          <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}
            placeholder="target data-testid (e.g. nav-analytics)" data-testid="tourmgr-new-target" className={`${inputCls} font-mono`} />
        </div>
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
          rows={2} placeholder="Short description shown in the bubble" data-testid="tourmgr-new-body" className={inputCls} />
        <div className="flex gap-2">
          <input value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })}
            placeholder="route (optional, for the future AI guide)" data-testid="tourmgr-new-route" className={`${inputCls} font-mono`} />
          <button onClick={add} disabled={!form.title.trim() || !form.target.trim()} data-testid="tourmgr-add-btn"
            className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-4 text-xs font-bold text-black disabled:opacity-50 shrink-0">
            <Plus className="h-3.5 w-3.5" /> Add step
          </button>
        </div>
      </div>

      <GuideVideos />
    </div>
  );
}
